import actionTypes, * as actionGroups from "custom-contacts-upload/action-types"
import * as djangioActionCreators from "shared/djangio/action-creators"
import urls from "imports/desktopPaths"
import BACKENDERROR from "app/static/frontend/imports/backenderror"

const createRequest = (dispatch, type, url, data) => () =>
    new Promise((resolve, reject) => {
        dispatch({ type: type.start })
        const xhr = new XMLHttpRequest()
        xhr.open("POST", url)
        /* eslint-disable no-undef */
        xhr.setRequestHeader("X-CSRFToken", csrf_token)
        /* eslint-enable no-undef */
        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve(xhr.response)
            } else if (xhr.status === 400) {
                reject(xhr.response)
            } else {
                reject(xhr.status)
            }
        }
        xhr.onerror = (error) => {
            reject(error)
        }
        xhr.send(data)
    })

const handleRequest = (dispatch, request, type, updatingFn, onFailFn, handleResponse) => {
    request()
        .then((response) => {
            const parsed = handleResponse(response)
            updatingFn(parsed)
            dispatch({ type: type.success, parsed })
        })
        .catch((error) => {
            dispatch({ type: type.fail, error })
            onFailFn(error)
        })
}

const createAndPostRequest = (dispatch, type, url, data, updatingFn, onFailFn, handleResponse = (r) => r) => {
    const request = createRequest(dispatch, type, url, data)
    handleRequest(dispatch, request, type, updatingFn, onFailFn, handleResponse)
}

/**
 * Upload a user's spreadsheet to S3.
 * Response (passed to updatingFn if upload is successful) is in format:
 *     {
 *         object_id: <number>,
 *         num_rows: <number>,
 *         file_name: <string>,
 *         file_headers: <array of strings>
 *     }
 *
 * @param  {FormData} formData      [FormData object with the spreadsheet file at key 'user-file']
 * @param  {number} formTypeValue   [a BulkUploadType enum value]
 * @param  {func} updatingFn        [function called with parsed json response (shown above) if upload is successful]
 * @param  {func} onFailFn          [function called with error string if the upload fails]
 */
export const uploadFile = (formData, formTypeValue, updatingFn, onFailFn) => (dispatch) => {
    createAndPostRequest(
        dispatch,
        actionGroups.uploadFile,
        `${urls.uploadFile}${formTypeValue}/`,
        formData,
        updatingFn,
        onFailFn,
        (response) => JSON.parse(response),
    )
}

/**
 * Upload the spreadsheet column headers, which are internal quorum variables.
 * Format of data arg:
 *     [FormData object] {
 *         id: <upload id from uploadFile response>,
 *         'quorum-headers': <object mapping user column header (string) to a quorum field
 *             (value of enum corresponding to BulkUploadType (e..g CustomContactsField))>
 *     }
 *
 * @param  {FormData} data [FormData object with upload id at key 'id' and headers at key 'quorum-headers' (format shown above)]
 */
export const uploadHeaders = (data) => async (dispatch) => {
    dispatch({ type: actionGroups.uploadHeaders.start })

    const response = await fetch(urls.uploadHeaders, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "x-csrftoken": Userdata.csrftoken,
        },
        body: JSON.stringify({ data }),
    })

    if (!response.ok) {
        dispatch({ type: actionGroups.uploadHeaders.fail })
        const error = await response.json()
        throw new Error(error.message, { cause: error.reason })
    }

    dispatch({ type: actionGroups.uploadHeaders.success })
}

/**
 * Match quorum field enum value to the current selected user field
 * @param  {number} quorumField [value of enum corresponding to BulkUploadType (e..g CustomContactsField)]
 */
export const makeMatchOnSelected = (quorumField) => ({
    type: actionGroups.matchFields.makeMatchOnSelected,
    quorumField,
})

export const makeMatch = (quorumField, userField) => ({
    type: actionGroups.matchFields.makeMatch,
    quorumField,
    userField,
})

export const removeMatch = (userField) => ({
    type: actionGroups.matchFields.removeMatch,
    userField,
})

export const selectNextUserField = () => ({
    type: actionGroups.matchFields.selectNextUserField,
})

export const resetState = () => ({
    type: actionGroups.progress.resetState,
})

const { BulkUploadFile } = DjangIO.app.custom_data.models
/**
 * This action creator makes an Action Http request to the backend
 * Upon success, sets
 * @name getBulkUploadLinkForResourceTable
 * @function
 * @param {Immutable} Object - An object with entity id
 */
export const getBulkUploadLinkForResourceTable = (datum) => async (dispatch) => {
    const bulkUploadId = datum.get("id")
    try {
        const response = await dispatch(
            djangioActionCreators.resourceGetDetail(
                BulkUploadFile.objects.action("get_bulk_upload_link_for_resource_table"),
                actionGroups.getBulkUploadLinkForResourceTableRow,
                bulkUploadId,
                {
                    id: bulkUploadId,
                },
            ),
        )

        window.location.href = response.link
    } catch (error) {
        BACKENDERROR(error, {
            icon: "error",
            title: "Error Generating Link",
            text:
                (error && error.response && error.response.data) || "There was an issue generating a link to this file",
        })
    }
}

export const changeUploadType = (uploadType) => ({
    type: actionTypes.CHANGE_UPLOAD_TYPE,
    uploadType,
})

export const editLimitedToTeams = (limitedToTeams) => ({
    type: actionTypes.EDIT_LIMITED_TO_TEAMS,
    limitedToTeams,
})

export const editIsVisibleToPartners = (is_visible_to_partners) => ({
    type: actionTypes.EDIT_IS_VISIBLE_TO_PARTNERS,
    is_visible_to_partners: is_visible_to_partners,
})
